import { useEffect, useRef, useState, FC, useCallback, useTransition } from 'react';
import { Box, CircularProgress, Grid, IconButton } from '@mui/material';

// components & types
import { ThumbnailList } from '../thumbnail-list';
import Player from 'components/player';
import ItemDescription from '../item-description';
import ArrowLeftIcon from 'assets/icons/viewer-arrow-left.svg';
import ArrowRightIcon from 'assets/icons/viewer-arrow-right.svg';
import { IViewer } from '../../entities';
import { ImageDetails } from './components/image-details';
import CloseIcon from 'assets/icons/viewer-close.svg';
import { styles } from './styes';
import { Author } from './components/author/author';

type TViewerProps = Omit<IViewer, 'isOpen'>;

export const Viewer: FC<TViewerProps> = ({
  imagesList,
  activeIndex,
  handleClose = () => {},
  editablePostData,
  isDescriptionEditable = false,
  updateDescription,
  fetchingProps,
  isActivityFeedMedia,
}) => {
  const [_, startTransition] = useTransition();

  const { fetchData, isFetching, hasMore } = fetchingProps ?? {};

  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  const [imageSrcIndex, setImageSrcIndex] = useState<number>(activeIndex ?? imagesList.length - 1);
  const isKeyboardRef = useRef<boolean>(false);
  const currentItem = imagesList[imageSrcIndex];
  const isLeftDisabled = imageSrcIndex === 0;
  const isRightDisabled = imageSrcIndex === imagesList.length - 1 && !hasMore;

  const handleLeft = () => {
    if (isLeftDisabled) return;

    isKeyboardRef.current = true;
    setIsMediaLoaded(false);

    startTransition(() => {
      setImageSrcIndex((prev) => prev - 1);
    });
  };

  const handleRight = useCallback(() => {
    if (imageSrcIndex + 1 > imagesList.length - 1) return;

    if (hasMore && !isFetching && imageSrcIndex > imagesList.length - 5) {
      fetchData?.();
    }

    isKeyboardRef.current = true;
    setIsMediaLoaded(false);

    startTransition(() => {
      setImageSrcIndex((prev) => prev + 1);
    });
  }, [imageSrcIndex, imagesList.length, fetchData, hasMore, isFetching]);

  useEffect(() => {
    const keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') handleRight();
      if (e.key === 'ArrowLeft') handleLeft();
      if (e.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', keydownHandler);

    return () => document.removeEventListener('keydown', keydownHandler);
  }, [handleRight, handleLeft, handleClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const isVideo = currentItem?.mimeType?.includes('video');

  const shouldRenderDescription =
    !isVideo && (editablePostData || updateDescription) && currentItem?.itemId && isMediaLoaded;

  return (
    <Box sx={styles.container}>
      {/* HEADER */}
      <Box sx={styles.header}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* BODY */}
      <Box sx={styles.body}>
        {/* Left Arrow */}
        {!isLeftDisabled && (
          <IconButton onClick={handleLeft} sx={styles.navLeft}>
            <ArrowLeftIcon />
          </IconButton>
        )}

        <Box sx={styles.mediaWrapper}>
          <Box sx={styles.mediaContent}>
            {!isMediaLoaded && !isVideo && (
              <Grid sx={styles.loader}>
                <CircularProgress sx={styles.loaderColor} />
              </Grid>
            )}

            {isMediaLoaded && (
              <>
                {!isVideo && isActivityFeedMedia && (
                  <Author
                    avatar={currentItem?.profile?.avatar?.url}
                    createdAt={currentItem?.createdAt}
                    firstName={currentItem?.profile?.firstName}
                    lastName={currentItem?.profile?.lastName}
                  />
                )}

                <ImageDetails timestamp={currentItem?.timestamp} location={currentItem?.location} />

                {shouldRenderDescription && (
                  <ItemDescription
                    key={currentItem.id}
                    id={currentItem.itemId}
                    description={currentItem.description}
                    editablePostData={editablePostData}
                    isDescriptionEditable={isDescriptionEditable}
                    updateDescription={updateDescription}
                  />
                )}
              </>
            )}

            {isVideo ? (
              <Player
                key={currentItem.id}
                url={currentItem.imageSrc}
                controls
                playing
                width="100%"
                height="100%"
                onReady={() => setIsMediaLoaded(true)}
              />
            ) : (
              <Box
                key={currentItem.id}
                component="img"
                onLoad={() => setIsMediaLoaded(true)}
                src={currentItem.imageSrc}
                sx={{ ...styles.image, visibility: isMediaLoaded ? 'visible' : 'hidden' }}
              />
            )}
          </Box>
        </Box>

        {/* Right Arrow */}
        {!isRightDisabled && (
          <IconButton onClick={handleRight} sx={styles.navRight}>
            <ArrowRightIcon />
          </IconButton>
        )}
      </Box>

      <Box sx={styles.thumbnailBox}>
        <ThumbnailList
          isActivityFeedMedia={isActivityFeedMedia}
          isKeyboardRef={isKeyboardRef}
          thumbnailList={imagesList}
          activeIndex={imageSrcIndex}
          handleThumbnailPress={(idx) => {
            setIsMediaLoaded(false);
            isKeyboardRef.current = false;
            setImageSrcIndex(idx);
          }}
        />
      </Box>
    </Box>
  );
};
